import React from 'react'
import { Box, LinearProgress, Slider, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

export type PositionDistributionOption = {
  key: string
  text: string
}

export type PositionDistributionValue = {
  key: string
  percentage: number
}

type PositionPointDistributionProps = {
  title: string
  options: PositionDistributionOption[]
  values: PositionDistributionValue[]
  onChange: (values: PositionDistributionValue[]) => void
  helperText?: string
}

const clampPercentage = (value: number) => {
  if (Number.isNaN(value)) return 0
  if (value < 0) return 0
  if (value > 100) return 100
  return Math.round(value)
}

const PositionPointDistribution: React.FC<PositionPointDistributionProps> = ({
  title,
  options,
  values,
  onChange,
  helperText
}) => {
  const { t } = useTranslation()
  const normalizedValues = React.useMemo(() => {
    return options.map((option) => ({
      key: option.key,
      percentage: clampPercentage(values.find((x) => x.key === option.key)?.percentage ?? 0)
    }))
  }, [options, values])

  const total = React.useMemo(
    () => normalizedValues.reduce((sum, item) => sum + item.percentage, 0),
    [normalizedValues]
  )

  const updateValue = (key: string, nextValue: number) => {
    const safeNext = clampPercentage(nextValue)
    const current = normalizedValues.find((item) => item.key === key)?.percentage ?? 0
    const maxAllowed = Math.max(0, 100 - (total - current))
    const applied = Math.min(safeNext, maxAllowed)

    const next = normalizedValues.map((item) =>
      item.key === key ? { ...item, percentage: applied } : item
    )
    onChange(next)
  }

  return (
    <Box sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('positions.form.progress')}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {total}/100
        </Typography>
      </Box>

      <LinearProgress
        variant="determinate"
        value={Math.min(total, 100)}
        color={total === 100 ? 'success' : 'primary'}
        sx={{ height: 8, borderRadius: 4, mb: 2 }}
      />

      <Box sx={{ display: 'grid', gap: 2 }}>
        {options.map((option) => {
          const value = normalizedValues.find((item) => item.key === option.key)?.percentage ?? 0

          return (
            <Box key={option.key}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {option.text}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 92px', alignItems: 'center', gap: 2 }}>
                <Slider
                  value={value}
                  min={0}
                  max={100}
                  step={1}
                  valueLabelDisplay="auto"
                  onChange={(_, next) => updateValue(option.key, Number(next))}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 100 }}
                  value={value}
                  onChange={(event) => updateValue(option.key, Number(event.target.value))}
                />
              </Box>
            </Box>
          )
        })}
      </Box>

      {helperText ? (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {helperText}
        </Typography>
      ) : null}
    </Box>
  )
}

export default PositionPointDistribution
